// ────────────────────────────────────────────────────────────────
//  errorLogger.js
//  Client-side auto-capture shim. Installs global listeners and a
//  single `logError(payload)` helper that posts to /api/error-logs.
//
//  What triggers it:
//    • window.onerror                — synchronous JS exceptions
//    • unhandledrejection            — async promise rejections
//    • ErrorBoundary.componentDidCatch — React render errors
//    • Axios response interceptor    — 5xx API responses
//    • Manual call from any component
//
//  De-duplication: if the same (message + short stack-head) fires
//  repeatedly, we log the first one and suppress duplicates for
//  DEDUP_WINDOW_MS. Prevents render-loop spam from flooding the DB.
//
//  Failure mode: logError NEVER throws. If the POST fails we
//  console.warn and move on. The app must never crash because
//  logging crashed.
// ────────────────────────────────────────────────────────────────

const API_BASE =
  (typeof process !== 'undefined' && process.env && process.env.REACT_APP_BACKEND_URL)
    ? process.env.REACT_APP_BACKEND_URL
    : '';

const DEDUP_WINDOW_MS = 60_000; // 1 minute
const recentSignatures = new Map();
let installed = false;

function makeSignature(message, stackHead) {
  return `${String(message).slice(0, 200)}::${String(stackHead || '').slice(0, 200)}`;
}

function isDuplicate(sig) {
  const now = Date.now();
  for (const [k, t] of recentSignatures) {
    if (now - t > DEDUP_WINDOW_MS) recentSignatures.delete(k);
  }
  if (recentSignatures.has(sig)) return true;
  recentSignatures.set(sig, now);
  return false;
}

/**
 * Post an error entry to /api/error-logs. Safe to call from anywhere.
 * Payload shape:
 *   { message, details?, stack_trace?, origin?, severity?, url?, context? }
 * origin defaults: 'frontend_exception'. severity defaults: 'error'.
 */
export async function logError(payload = {}) {
  try {
    const message = payload.message || 'Unknown client error';
    const stack = payload.stack_trace || '';
    const sig = makeSignature(message, stack.split('\n')[0] || '');
    if (isDuplicate(sig)) return;

    // Short-circuit if the user isn't authenticated. /api/error-logs
    // requires auth — posting anonymously just wastes a round-trip on a
    // 403, and in tight boot-sequence loops can contribute to console
    // spam. Errors on the login page are typically deterministic and
    // reproducible without the log anyway.
    const token = typeof localStorage !== 'undefined' ? localStorage.getItem('token') : null;
    if (!token) {
      if (typeof console !== 'undefined') {
        console.debug('[errorLogger] skipped (no auth token):', String(message).slice(0, 120));
      }
      return;
    }

    const body = {
      severity: payload.severity || 'error',
      origin: payload.origin || 'frontend_exception',
      message: String(message).slice(0, 500),
      details: payload.details ? String(payload.details).slice(0, 2000) : null,
      stack_trace: stack ? String(stack).slice(-3000) : null,
      url: typeof window !== 'undefined' ? String(window.location.href).slice(0, 500) : null,
      user_agent: typeof navigator !== 'undefined' ? String(navigator.userAgent).slice(0, 300) : null,
      affected_files: Array.isArray(payload.affected_files) ? payload.affected_files.slice(0, 10) : [],
      context: payload.context || null,
    };

    // We fire-and-forget so the caller (an error handler or interceptor)
    // never waits on network. fetch() is used directly to avoid going
    // through the same axios instance we might be instrumenting.
    fetch(`${API_BASE}/api/error-logs`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        Authorization: `Bearer ${token}`,
      },
      body: JSON.stringify(body),
      // Don't let a slow log request block or error-surface anywhere:
      keepalive: true,
    }).catch((e) => {
      // Intentionally silent — the surrounding app must not break if the
      // error sink is unavailable.
      if (typeof console !== 'undefined') {
        console.warn('[errorLogger] POST failed:', e && e.message);
      }
    });
  } catch (_e) {
    // Swallow — logging must never raise.
  }
}

/**
 * Install the global browser listeners. Idempotent — safe to call more
 * than once; only the first call takes effect. Typical call site:
 * frontend/src/index.js, once at module load.
 */
export function installGlobalErrorLogger() {
  if (installed || typeof window === 'undefined') return;
  installed = true;

  window.addEventListener('error', (ev) => {
    try {
      // Filter out ResizeObserver loop warnings — they're benign noise
      // and flood the log with nothing actionable.
      const msg = ev && ev.message ? String(ev.message) : 'window error';
      if (msg.includes('ResizeObserver loop')) return;

      logError({
        severity: 'error',
        origin: 'frontend_exception',
        message: msg,
        details: ev && ev.filename ? `${ev.filename}:${ev.lineno || '?'}:${ev.colno || '?'}` : null,
        stack_trace: ev && ev.error && ev.error.stack ? ev.error.stack : null,
      });
    } catch (_e) { /* never raise */ }
  });

  window.addEventListener('unhandledrejection', (ev) => {
    try {
      const reason = ev && ev.reason;
      const message = (reason && (reason.message || reason.toString && reason.toString())) || 'Unhandled promise rejection';
      const stack = reason && reason.stack ? reason.stack : null;
      logError({
        severity: 'error',
        origin: 'promise_rejection',
        message: String(message),
        stack_trace: stack,
      });
    } catch (_e) { /* never raise */ }
  });
}

export default { logError, installGlobalErrorLogger };
